"use client";

import { gsap } from "gsap";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ContactHeroSection = () => {
  const root = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.from(".hero-eyebrow", {
        y: 20,
        opacity: 0,
        duration: 0.7,
      })
        .from(
          ".hero-title",
          {
            y: 50,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
          },
          "-=0.4",
        )
        .from(
          ".hero-sub",
          {
            y: 30,
            opacity: 0,
            duration: 0.9,
          },
          "-=0.5",
        );

      gsap.to(imageRef.current, {
        yPercent: 10,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="relative hero-section w-full overflow-hidden bg-background-primary !px-4">
      {/* Mobile background (light) */}
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px] dark:hidden">
        <Image src="/images/serviceBanners/LetsTakhW.webp" alt="Contact Us" fill priority className="object-cover object-right" />
      </div>
      {/* Mobile background (dark) */}
      <div className="relative hidden w-full h-[260px] sm:h-[340px] dark:block lg:dark:hidden">
        <Image src="/images/serviceBanners/contactus.webp" alt="Contact Us" fill priority className="object-cover object-right" />
      </div>

      {/* Desktop background (wrapper with ref={imageRef} for GSAP) */}
      <div ref={imageRef} className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image src="/images/serviceBanners/LetsTakhW.webp" alt="Contact Us" fill priority className="object-contain object-right dark:hidden" />
        <Image src="/images/serviceBanners/contactus.webp" alt="Contact Us" fill priority className="object-contain object-right hidden dark:block" />
      </div>

      {/* Light Overlay */}
      <div
        className="absolute inset-0 z-10 hidden lg:block dark:hidden"
        style={{
          background:
            "linear-gradient(90deg, rgba(240, 242, 255, 0.95) 0%, rgba(240, 242, 255, 1) 45%, rgba(240, 242, 255, 0.9) 50%, rgba(240, 242, 255, 0.25) 65%,  transparent 100%)",
        }}
      />
      {/* Dark Overlay */}
      <div
        className="absolute inset-0 z-10 hidden dark:lg:block"
        style={{
          background: "linear-gradient(90deg, #0d0f1e 0%, #0d0f1e 28%, rgba(13, 15, 30, 1) 15%, rgba(13, 15, 30, 0.3) 65%, transparent 100%)",
        }}
      />

      <div className="relative z-20 flex flex-col justify-center  px-6!  !py-10">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-4! hero-eyebrow">
            <h2 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">CONTACT US</h2>

            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-400 to-purple-600 rounded-full"></span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Have Questions?</span>

            <br />

            <span className="hero-title text-text-primary">We&apos;re Here.</span>
          </h1>

          <p className="hero-sub text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            Empower your business with innovative digital solutions designed to accelerate growth, improve customer engagement, and strengthen your online presence. From custom web
            and mobile application development to AI-powered solutions, UI/UX design, digital marketing, ecommerce platforms, and enterprise software services, our team helps
            businesses transform ideas into scalable digital experiences built for long-term success.
          </p>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default ContactHeroSection;
